import { AdminGetRoutesResDto } from "service/api/estabrookSchemas";
import { DateTime } from "luxon";
import { getRouteEstimatedTimeMinutes } from "./route-estimate";

export const TIMETABLE_ITEM_WIDTH = 60;

// Offset to move the starting point outside the timeline start
// Starting point dot (8px) + line (12px) - overlap (8px) = 12px
export const STARTING_POINT_OFFSET = 12;

export type RoutesMinAndMaxStartTime = {
  min: string | null;
  max: string | null;
};

export const getMinAndMaxTime = (
  routes: AdminGetRoutesResDto[],
): { min: string | null; max: string | null; totalHoursFromStart?: number } => {
  const startTimes = routes
    .map((route) => route.startTime)
    .sort((a, b) => {
      // Sort by time value, not lexicographically
      const timeA = new Date(`1970-01-01T${a}:00`).getTime();
      const timeB = new Date(`1970-01-01T${b}:00`).getTime();
      return timeA - timeB;
    });

  if (startTimes.length === 0) {
    return { min: null, max: null };
  }

  let maxEndTime = DateTime.fromISO("1970-01-01T00:00:00");

  routes.forEach((route) => {
    const startTime = DateTime.fromFormat(route.startTime, "HH:mm", {
      zone: "UTC",
    });

    // Use estimatedTimeMinutes for total route duration (includes travel + service)
    // Fall back to serviceTimeMin if not available, then to 1 minute
    const totalDurationMin =
      getRouteEstimatedTimeMinutes(route) ??
      route.travelDetails?.serviceTimeMin ??
      1;
    const endTime = startTime.plus({ minutes: totalDurationMin });

    if (endTime > maxEndTime) {
      maxEndTime = endTime;
    }
  });

  // Ensure minimum 24-hour timeline by calculating minimum end time
  // Floor to the hour so the timeline origin lines up with the hourly header
  // slots even when the earliest route starts on a half hour (e.g. 10:30)
  const earliestStartTime = DateTime.fromFormat(startTimes[0], "HH:mm", {
    zone: "UTC",
  }).startOf("hour");
  const minimumEndTime = earliestStartTime.plus({ hours: 24 });

  // Use the later of actual max end time or minimum 24-hour end time
  const finalMaxEndTime =
    maxEndTime > minimumEndTime ? maxEndTime : minimumEndTime;

  // Calculate total hours from start to end
  const totalHoursFromStart = finalMaxEndTime.diff(
    earliestStartTime,
    "hours",
  ).hours;

  return {
    // Keep the raw value if it can't be parsed, matching the previous behavior
    min: earliestStartTime.isValid
      ? earliestStartTime.toFormat("HH:mm")
      : startTimes[0],
    max: finalMaxEndTime.toFormat("HH:mm"),
    totalHoursFromStart: Math.ceil(totalHoursFromStart),
  };
};

export const generateHourlySlots = (
  routes: AdminGetRoutesResDto[],
): string[] => {
  const { min, totalHoursFromStart } = getMinAndMaxTime(routes);

  if (!min || !totalHoursFromStart) {
    return [];
  }

  // Calculate the start hour
  const startHour = new Date(`1970-01-01T${min}:00`).getHours();

  // Calculate end hour based on total hours from start
  const endHour = startHour + totalHoursFromStart;

  // Generate hourly slots
  const hours: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    hours.push(
      new Date(1970, 0, 1, hour % 24).toLocaleTimeString([], {
        hour: "numeric",
        hour12: true,
      }),
    );
  }

  return hours;
};

// New function for zoom-aware hourly slots generation
export const generateZoomAwareHourlySlots = (
  routes: AdminGetRoutesResDto[],
  visibleHours: number,
  startHour?: number,
): string[] => {
  const { min } = getMinAndMaxTime(routes);

  // If no routes, start from a default hour
  const defaultStartHour = min
    ? new Date(`1970-01-01T${min}:00`).getHours()
    : 6; // Default to 6 AM

  const actualStartHour = startHour ?? defaultStartHour;
  const endHour = actualStartHour + visibleHours;

  // Generate hourly slots for the visible range
  const hours: string[] = [];
  for (let hour = actualStartHour; hour < endHour; hour++) {
    hours.push(
      new Date(1970, 0, 1, hour % 24).toLocaleTimeString([], {
        hour: "numeric",
        hour12: true,
      }),
    );
  }

  return hours;
};

/**
 * Calculates the left position of a route on the timeline.
 *
 * @param {string} startTime - The start time of the route in "HH:mm" format.
 * @param {string} firstTimelineItem - The start time of the first timeline slot in "HH:mm" format.
 * @param {number} hourTimelineWidth - The width of each hour slot in pixels (default: 180px).
 * @param {number} zoomLevel - The zoom level multiplier (default: 1.0).
 * @returns {number} - The calculated left position in pixels.
 */
export const calculateLeftPosition = (
  startTime: string,
  firstTimelineItem: string | null,
  hourTimelineWidth = TIMETABLE_ITEM_WIDTH,
  zoomLevel = 1.0,
) => {
  const parseTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes; // Convert time to total minutes
  };

  const startMinutes = parseTime(startTime);
  const firstSlotMinutes = parseTime(firstTimelineItem || "");

  const differenceInMinutes = startMinutes - firstSlotMinutes;
  // Round to nearest pixel to avoid sub-pixel rendering issues
  return Math.round((differenceInMinutes / 60) * hourTimelineWidth * zoomLevel);
};

/**
 * Calculates the width of a box based on its duration.
 *
 * @param {number} durationInMinutes - The duration of the box in minutes.
 * @param {number} timelineItemWidth - The width of an hour slot on the timeline in pixels (default: 60px).
 * @param {number} zoomLevel - The zoom level multiplier (default: 1.0).
 * @param {boolean} precise - If true, returns exact width without rounding (for live phase).
 *
 * @returns {number} - The calculated width of the box in pixels.
 */
export const calculateBoxWidth = (
  durationInMinutes: number,
  timelineItemWidth = TIMETABLE_ITEM_WIDTH,
  zoomLevel = 1.0,
  precise = false,
): number => {
  // Convert duration to the proportion of an hour, then multiply by the width of an hour slot
  // Formula: (minutes / 60) * pixelsPerHour * zoomLevel
  // This ensures 1 minute = (hourWidth / 60) pixels at zoom level 1.0
  // No minimum width - boxes should fit proportionally within the timeline
  const width = (durationInMinutes / 60) * timelineItemWidth * zoomLevel;
  // In live phase (precise=true), return exact width for accurate timeline representation
  // Otherwise, round to nearest pixel to avoid sub-pixel rendering issues
  return precise ? width : Math.round(width);
};
